import React, { useMemo, useRef } from "react";
import { Platform, View } from "react-native";
import { Display, mq } from "react-native-unistyles";

import Image from "$src/core/components/atoms/Image/Image";
import Text from "$src/core/components/atoms/Text/Text";
import useResponsiveImage from "$src/core/hooks/use-responsive-image";
import type { ImageDto } from "$src/core/models/dto";
import pickImageUrl from "$src/core/utils/image/pick-image-url";

import $, { IMAGE_DIMENSIONS } from "./SearchResultCard.styles";

type Props = {
  title?: string;
  description?: string;
  image?: ImageDto;
  progress?: string | number;
};

const SearchResultCard: React.FC<Props> = ({
  title,
  description,
  image,
  progress,
}) => {
  const ref = useRef<View | null>(null);

  const cover = useMemo(
    () => ({
      uri: image ? pickImageUrl(image, ["landscape", "portrait"]) : undefined,
      dimensions: IMAGE_DIMENSIONS.cover,
    }),
    [image],
  );

  const responsiveCoverImage = useResponsiveImage(cover.uri, {
    dimensions: cover.dimensions,
  });

  return (
    <View ref={ref} style={$.container}>
      <View style={$.card}>
        {responsiveCoverImage && (
          // This is a decorative image, so we don't need an accessibility label
          // eslint-disable-next-line triple-rn-a11y/accessibility-label
          <Image
            accessibilityRole="none"
            source={responsiveCoverImage}
            contentFit="cover"
            style={$.image}
          />
        )}
        {typeof progress === "string" && (
          <View style={$.duration}>
            <Text accessibilityRole="summary">{progress}</Text>
          </View>
        )}
        {typeof progress === "number" && (
          <View style={$.progressBar}>
            <View style={[{ width: `${progress}%` }, $.progressAmount]} />
          </View>
        )}
      </View>

      <View style={$.meta}>
        <Display mq={mq.only.width(undefined, "sm")}>
          <Text
            preset="heading5"
            accessibilityRole={Platform.OS === "web" ? "text" : "menuitem"}
          >
            {title}
          </Text>
        </Display>
        <Display mq={mq.only.width("sm")}>
          <Text
            preset="heading5"
            accessibilityRole={Platform.OS === "web" ? "text" : "menuitem"}
            numberOfLines={1}
          >
            {title}
          </Text>
        </Display>

        <Display mq={mq.only.width(undefined, "md")}>
          <Text
            accessibilityRole="text"
            preset="tiny"
            numberOfLines={2}
            style={$.description}
          >
            {description}
          </Text>
        </Display>
        <Display mq={mq.only.width("md")}>
          <Text
            accessibilityRole="text"
            preset="body"
            numberOfLines={2}
            style={$.description}
          >
            {description}
          </Text>
        </Display>
      </View>
    </View>
  );
};

export default SearchResultCard;
